import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { HeroContent } from './HeroContent';

interface HeroProps {
  children: React.ReactNode; // Invoice form content
  invoiceControls?: React.ReactNode; // Controls for the invoice (New, Settings)
  afterContent?: React.ReactNode; // Content to render after the invoice form
  showHeroProps?: boolean; // Whether to show the hero props (tagline and particles)
}

export const Hero: React.FC<HeroProps> = ({ 
  children, 
  invoiceControls, 
  afterContent,
  showHeroProps = true 
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);

  return (
    <>
      {/* Hero Section */}
      <div
        ref={parentRef}
        className="relative invoice-hero-container flex min-h-screen flex-col overflow-hidden items-center justify-center px-4 pt-20 md:px-8 md:pt-40 bg-neutral-50 dark:bg-neutral-900"
      >
        <HeroContent 
          containerRef={containerRef} 
          parentRef={parentRef} 
          showHeroProps={showHeroProps}
        />
        
        {/* Invoice Controls - Rendered above the invoice form */}
        {invoiceControls && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.8 }}
            className="relative z-[60] mt-20 mx-auto w-full max-w-3xl xl:min-w-5xl xl:max-w-5xl"
          >
            <div className="flex items-center justify-between">
              {invoiceControls}
            </div>
          </motion.div>
        )}
        
        {/* Invoice Form Container */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.9, ease: "easeOut" }}
          ref={containerRef}
          className="relative z-50 mx-auto w-full md:max-w-3xl xl:max-w-5xl xl:w-full rounded-[32px] border border-neutral-200/50 bg-neutral-100 p-2 backdrop-blur-lg dark:border-neutral-700 dark:bg-neutral-800/50 md:p-4"
        >
          <div 
            className="invoice-container-inner rounded-[24px] border border-neutral-200 bg-white p-2 dark:border-neutral-700 dark:bg-black"
          >
            {children}
          </div>
        </motion.div>
        
        {/* After Content - Rendered after the invoice form but still inside the hero container */}
        {afterContent && (
          <div className="w-full">
            {afterContent}
          </div>
        )}
      </div>
    </>
  );
}; 